// Public landing page (/), in the 1.3″ design language (Figma 4WuszrwOR3NjXeKumtTdgo, node 35:2). Powerful, always on,
// feels local: the problem (agents and dev work need 24/7, a laptop sleeps, a VPS is painful), what cmx gives, the cloud
// computer, how much more it gets done, how it feels local, Magic Setup, the phone, cmx next to a laptop and a VPS, pricing, questions, then getting
// started. Static sections live in landing/Sections.tsx, their behaviour in landing/fx.ts, and every style in
// landing/landing.css (scoped under #lp).

import { useEffect, useRef, type ReactNode } from 'react';
import { Link } from '../router';
import { CmxMark } from '../ui/CmxMark';
import { useStore } from '../store';
import { refreshMe } from '../session';
import { Band, Both, Cloud, Compare, Faq, FromPrice, Local, Multiply, Phone, Pricing, Problem, Setup, Sprites } from './landing/Sections';
import { mountLanding } from './landing/fx';
import { HeroArt } from './landing/HeroArt';
import './landing/landing.css';


const Marks = () => (
  <>
    <span className="mk mk-l" aria-hidden="true" />
    <span className="mk mk-r" aria-hidden="true" />
  </>
);

const Logo = () => (
  <CmxMark />
);

// the wordmark: cmx_ in Geist Pixel, the underscore a resting cursor
const Wordmark = () => (
  <span className="wm" aria-hidden="true">
    cmx<span className="wm-u">_</span>
  </span>
);

const Arrow = () => (
  <svg className="arr" aria-hidden="true" width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M2.5 7H11.5M8 10.5L11.5 7L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function Header({ signedIn }: { signedIn: boolean }) {
  return (
    <header className="bar">
      <Marks />
      <div className="plate">
        <a className="brand brand-mk cmx-lockup" href="#top" aria-label="cmx home">
          <Logo />
          <Wordmark />
        </a>
        <nav className="nav" aria-label="Main">
          <a href="#problem" className="nav-link hide-sm">
            Why cmx
          </a>
          <a href="#cloud" className="nav-link hide-sm">
            Cloud computer
          </a>
          <a href="#local" className="nav-link hide-sm">
            Feels local
          </a>
          <a href="#pricing" className="nav-link hide-sm">
            Pricing
          </a>
          {signedIn ? (
            <Link to="/dashboard" className="btn btn-primary" style={{ marginLeft: 8 }} data-testid="nav-dashboard">
              Dashboard
            </Link>
          ) : (
            <>
              <Link to="/login" className="btn" style={{ marginLeft: 8 }} data-testid="nav-signin">
                Sign in
              </Link>
              <Link to="/signup" className="btn btn-primary" data-testid="nav-signup">
                Get started
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}

function Hero({ signedIn }: { signedIn: boolean }) {
  return (
    <section className="hero" id="top" aria-labelledby="hero-title">
      <div className="plate hero-p">
        <div className="hero-c" id="hero-copy">
          <p className="badge in" style={{ ['--d' as string]: '60ms' }}>
            <span className="badge-dot" aria-hidden="true" />
            Your next dev machine lives in the cloud
          </p>
          <div className="hero-t">
            <h1 className="h1 h1-one" id="hero-title">
              <span className="w" style={{ ['--i' as string]: 0 }}>
                Powerful.
              </span>{' '}
              <span className="w" style={{ ['--i' as string]: 1 }}>
                Always
              </span>{' '}
              <span className="w" style={{ ['--i' as string]: 2 }}>
                on.
              </span>
              <span className="w hlw" style={{ ['--i' as string]: 3 }}>
                <span className="hl">
                  <span className="hl-t">Feels local.</span>
                  <span className="blk" aria-hidden="true" />
                </span>
              </span>
            </h1>
            <p className="h1-sub in" style={{ ['--d' as string]: '360ms' }}>
              Cloud Machines for Your Agents and Dev Work
            </p>
          </div>
          <div className="hero-cta in" style={{ ['--d' as string]: '500ms' }}>
            <Link to={signedIn ? '/dashboard' : '/signup'} className="btn btn-primary btn-hero" data-testid="hero-cta">
              {signedIn ? 'Go to your dashboard' : 'Get started'}
            </Link>
            <a href="#pricing" className="btn btn-quiet btn-hero btn-in">
              See pricing <Arrow />
            </a>
          </div>
          <p className="yc in" style={{ ['--d' as string]: '620ms' }}>
            <span>BACKED BY</span>
            <span className="yc-b">
              <img src="/landing/yc.png" width="16" height="16" alt="" />
              <span>Y COMBINATOR</span>
            </span>
          </p>
        </div>
      </div>
      <HeroArt />
      <Band />
    </section>
  );
}

const Rings = () => (
  <span className="t-rings">
    <i />
    <i />
    <i />
  </span>
);

// the card sits in a window like the Mac app under "Every screen": a labelled tab, a title bar, the body, a status line
function Window({ status, children }: { status: string; children: ReactNode }) {
  return (
    <div className="panel wl-win">
      <div className="p-head">
        <svg className="carets" width="30" height="5" aria-hidden="true">
          <use href="#carets" />
        </svg>
        Get started
        <svg className="carets" width="30" height="5" aria-hidden="true">
          <use href="#carets" />
        </svg>
      </div>
      <div className="appbar" aria-hidden="true">
        <Rings />
        <span className="at">New cloud machine</span>
        <Rings />
      </div>
      {children}
      <div className="p-foot">
        <span className="ink">
          <span className="dot" />
          {status}
        </span>
        <span className="deco bars" aria-hidden="true" />
      </div>
    </div>
  );
}

function Access({ signedIn }: { signedIn: boolean }) {
  return (
    <section className="sec" id="get-started" aria-labelledby="access-title">
      <Marks />
      <div className="plate access">
        <div>
          <p className="kicker">
            <span data-dc="">// Get started</span>{' '}
            <svg className="carets" width="36" height="5" aria-hidden="true">
              <use href="#carets" />
            </svg>
          </p>
          <h2 className="h2" id="access-title">
            <span className="ln">Get your</span> <span className="ln">cloud computer.</span>
          </h2>
          <p className="lead">Make your account, pick a size, and your cloud machine is ready about two minutes after you pay.</p>
          <div className="racks" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <img key={i} src="/landing/rack-unit.svg" width="181" height="140" alt="" />
            ))}
          </div>
        </div>
        <div id="access-slot">
          <Window status="Ready about two minutes after you pay">
            <div className="wl-body access-card" data-testid="access-card">
              {signedIn ? (
                <Link to="/dashboard" className="btn btn-primary btn-lg btn-block">
                  Go to your dashboard <Arrow />
                </Link>
              ) : (
                <>
                  <Link to="/signup" className="btn btn-primary btn-lg btn-block" data-testid="access-signup">
                    Create your account <Arrow />
                  </Link>
                  <Link to="/login" className="btn btn-lg btn-block">
                    Sign in
                  </Link>
                </>
              )}
              <p className="small">
                <FromPrice />
                Cancel any time.
                <br />
                <Link to="/terms">Terms</Link> · <Link to="/privacy">Privacy</Link>
              </p>
            </div>
          </Window>
        </div>
      </div>
    </section>
  );
}

export function Landing() {
  const auth = useStore((s) => s.auth);
  // Know whether to offer "Dashboard" instead of "Sign in" (non-blocking).
  useEffect(() => {
    if (auth.kind === 'unknown') void refreshMe();
  }, [auth.kind]);
  const signedIn = auth.kind === 'in';
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // anchors land below the sticky bar; deep links like /#pricing exist only after the first render
    const html = document.documentElement;
    const prev = [html.style.scrollPaddingTop, html.style.scrollBehavior];
    html.style.scrollPaddingTop = '80px';
    const id = location.hash.slice(1);
    if (id) document.getElementById(id)?.scrollIntoView();
    html.style.scrollBehavior = 'smooth';
    const stop = root.current ? mountLanding(root.current) : undefined;
    return () => {
      stop?.();
      [html.style.scrollPaddingTop, html.style.scrollBehavior] = prev;
    };
  }, []);

  return (
    <div id="lp" className="v3" ref={root}>
      <Sprites />
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <div className="site">
        <Header signedIn={signedIn} />
        <main id="main">
          <Hero signedIn={signedIn} />
          <Problem />
          <Both />
          <Cloud />
          <Multiply />
          <Local />
          <Setup />
          <Phone />
          <Compare />
          <Pricing signedIn={signedIn} />
          <Faq />
          <Access signedIn={signedIn} />
        </main>
        {/* typed in by fx.ts: `cmx --help`, the link columns, the dot-matrix cmx_ wordmark */}
        <footer className="foot" />
      </div>
    </div>
  );
}
